import { useState } from "react";
import { api } from "../../api/apiClient";
import type { MeResponse } from "../../api/types";

/**
 * Self-service name and phone (owner decision 2026-09-10: "name and phone are
 * harmless to self-edit and it saves you being the bottleneck"). Job title is
 * an owner call and stays on the Team page; the sign-in email has its own
 * section beside this one (`ChangeEmailSection`). G-145: a section of
 * `EditProfilePanel` rather than a card of its own.
 */
export function MyDetailsSection({
  me,
  onSaved,
}: {
  me: MeResponse;
  onSaved: () => Promise<void>;
}) {
  const [firstName, setFirstName] = useState(me.firstName ?? "");
  const [lastName, setLastName] = useState(me.lastName ?? "");
  const [phoneNumber, setPhoneNumber] = useState(me.phoneNumber ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty =
    firstName.trim() !== (me.firstName ?? "") ||
    lastName.trim() !== (me.lastName ?? "") ||
    phoneNumber.trim() !== (me.phoneNumber ?? "");

  const submit = async () => {
    setError(null);
    setSaved(false);
    if (!firstName.trim() || !lastName.trim()) {
      setError("Name and surname are required.");
      return;
    }
    setSaving(true);
    try {
      await api.patchMyContact({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim() || null,
      });
      await onSaved();
      setSaved(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save your details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="profile-edit-section"
      aria-labelledby="profile-edit-details"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h3 id="profile-edit-details">My details</h3>
      {error ? <div className="alert error">{error}</div> : null}
      {saved && !dirty ? <div className="alert success">Saved.</div> : null}
      <div className="form-grid">
        <div className="field">
          <label htmlFor="my-first-name">Name *</label>
          <input
            id="my-first-name"
            value={firstName}
            maxLength={80}
            autoComplete="given-name"
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="my-last-name">Surname *</label>
          <input
            id="my-last-name"
            value={lastName}
            maxLength={80}
            autoComplete="family-name"
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="my-phone">Phone</label>
          <input
            id="my-phone"
            type="tel"
            value={phoneNumber}
            maxLength={40}
            autoComplete="tel"
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </div>
      </div>
      <div className="button-row">
        <button type="submit" className="button" disabled={saving || !dirty}>
          {saving ? "Saving…" : "Save my details"}
        </button>
      </div>
    </form>
  );
}
